/**
 * Secure Storage Service
 * HIPAA Compliance: Encrypts data before storing in browser storage
 * Regulation: 45 CFR 164.312(a)(2)(iv) - Encryption and Decryption
 *
 * This service:
 * - Encrypts all data before storing
 * - Uses sessionStorage (cleared when browser closes) for better security
 * - Provides a drop-in replacement for localStorage API
 */

import CryptoJS from 'crypto-js';

// Encryption key from environment variable
// IMPORTANT: In production, use a strong key and keep it secure
const getEncryptionKey = () => {
  const key = process.env.REACT_APP_STORAGE_ENCRYPTION_KEY;
  if (!key) {
    // Fallback key for development - NOT SECURE FOR PRODUCTION
    console.warn(
      '[secureStorage] REACT_APP_STORAGE_ENCRYPTION_KEY not set. Using fallback key. ' +
      'This is NOT secure for production!'
    );
    return 'mcm-visual-dev-key-change-in-production';
  }
  return key;
};

/**
 * Encrypts a value using AES encryption
 * @param {any} value - Value to encrypt (will be JSON stringified)
 * @returns {string} - Encrypted string
 */
const encrypt = (value) => {
  try {
    const stringValue = JSON.stringify(value);
    return CryptoJS.AES.encrypt(stringValue, getEncryptionKey()).toString();
  } catch (error) {
    console.error('[secureStorage] Encryption error:', error.message);
    return null;
  }
};

/**
 * Decrypts an AES encrypted value
 * @param {string} encryptedValue - Encrypted string
 * @returns {any} - Decrypted and parsed value
 */
const decrypt = (encryptedValue) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedValue, getEncryptionKey());
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    if (!decryptedString) {
      return null;
    }
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error('[secureStorage] Decryption error:', error.message);
    return null;
  }
};

/**
 * Secure Storage API
 * Uses sessionStorage for automatic cleanup when browser closes (more secure)
 * Falls back to memory storage if sessionStorage is not available
 */
const secureStorage = {
  /**
   * Store an encrypted value
   * @param {string} key - Storage key
   * @param {any} value - Value to store (will be encrypted)
   */
  setItem: (key, value) => {
    try {
      const encrypted = encrypt(value);
      if (encrypted) {
        sessionStorage.setItem(key, encrypted);
      }
    } catch (error) {
      console.error('[secureStorage] setItem error:', error.message);
    }
  },

  /**
   * Retrieve and decrypt a stored value
   * @param {string} key - Storage key
   * @returns {any} - Decrypted value or null
   */
  getItem: (key) => {
    try {
      const encrypted = sessionStorage.getItem(key);
      if (!encrypted) {
        return null;
      }
      return decrypt(encrypted);
    } catch (error) {
      console.error('[secureStorage] getItem error:', error.message);
      return null;
    }
  },

  /**
   * Remove a stored value
   * @param {string} key - Storage key
   */
  removeItem: (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('[secureStorage] removeItem error:', error.message);
    }
  },

  /**
   * Clear all stored values
   */
  clear: () => {
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('[secureStorage] clear error:', error.message);
    }
  },

  /**
   * Get all keys in storage
   * @returns {string[]} - Array of storage keys
   */
  keys: () => {
    try {
      return Object.keys(sessionStorage);
    } catch (error) {
      console.error('[secureStorage] keys error:', error.message);
      return [];
    }
  }
};

/**
 * Persistent Secure Storage API
 * Uses localStorage for data that needs to persist across browser sessions
 * Use with caution - data remains until explicitly cleared
 */
export const securePersistentStorage = {
  setItem: (key, value) => {
    try {
      const encrypted = encrypt(value);
      if (encrypted) {
        localStorage.setItem(key, encrypted);
      }
    } catch (error) {
      console.error('[securePersistentStorage] setItem error:', error.message);
    }
  },

  getItem: (key) => {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) {
        return null;
      }
      return decrypt(encrypted);
    } catch (error) {
      console.error('[securePersistentStorage] getItem error:', error.message);
      return null;
    }
  },

  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('[securePersistentStorage] removeItem error:', error.message);
    }
  },

  clear: () => {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('[securePersistentStorage] clear error:', error.message);
    }
  }
};

export default secureStorage;
